import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronLeft, ChevronRight, Eye, MessageSquare } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  getAdjacent,
  getGrievance,
  markAsViewed,
  type FilterStatus,
  type GrievanceAction,
  type SortDir,
} from "@/api/admin";
import { ApiError } from "@/api/client";
import { Spinner } from "@/components/Spinner";
import { formatDateTime } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";
import { AIResponseEditor } from "@/pages/admin/components/AIResponseEditor";
import { ApplicationStatusPanel } from "@/pages/admin/components/ApplicationStatusPanel";
import { AttachmentInline } from "@/pages/admin/components/AttachmentPanel";
import { HistoryDisclosure } from "@/pages/admin/components/HistoryDisclosure";
import { RightPanelDisclosure } from "@/pages/admin/components/RightPanelDisclosure";
import { useCurrentTabId } from "@/pages/admin/TabsContext";

const VALID_FILTERS: FilterStatus[] = ["Pending", "Acknowledged", "Attachments"];

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const grievanceId = Number(id);
  const tabId = useCurrentTabId();
  const nav = useNavigate();
  const [sp] = useSearchParams();

  // Filters carried in from the list URL. If the user deep-linked into the
  // detail page (no filters in URL), Prev/Next simply walks the unfiltered
  // sequence — same semantics as the list with no filter chips selected.
  const status = useMemo<FilterStatus[]>(
    () =>
      sp
        .getAll("status")
        .filter((s): s is FilterStatus => (VALID_FILTERS as string[]).includes(s)),
    [sp],
  );
  const searchQ = sp.get("q") ?? "";
  const sort: SortDir = sp.get("sort") === "asc" ? "asc" : "desc";
  const filterSearch = useMemo(() => {
    const u = new URLSearchParams();
    status.forEach((s) => u.append("status", s));
    if (searchQ) u.set("q", searchQ);
    if (sort === "asc") u.set("sort", "asc");
    const s = u.toString();
    return s ? `?${s}` : "";
  }, [status, searchQ, sort]);

  const q = useQuery({
    queryKey: ["grievance", tabId, grievanceId],
    queryFn: () => getGrievance(tabId, grievanceId),
    enabled: Number.isFinite(grievanceId) && !!tabId,
  });

  const qc = useQueryClient();
  const markViewed = useMutation({
    mutationFn: () => markAsViewed(tabId, grievanceId),
    onSuccess: () => {
      toast.success("Marked as viewed.");
      void qc.invalidateQueries({ queryKey: ["grievance", tabId, grievanceId] });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError
          ? e.message
          : "Couldn't be marked as viewed. Please try again.",
      ),
  });

  // Adjacency is computed against the govt DB *before* the reply lands, so
  // the "next" the editor picks up here is the next filtered ticket that
  // still exists as, e.g., Pending — exactly what we want on post-send jump.
  const adj = useQuery({
    queryKey: ["adjacent", tabId, grievanceId, status, searchQ, sort],
    queryFn: () => getAdjacent(tabId, grievanceId, { status, q: searchQ, sort }),
    enabled: Number.isFinite(grievanceId) && !!tabId,
  });

  const prevPath =
    adj.data?.prev_id != null ? `/admin/tickets/${adj.data.prev_id}${filterSearch}` : null;
  const nextPath =
    adj.data?.next_id != null ? `/admin/tickets/${adj.data.next_id}${filterSearch}` : null;

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader
        title="Admin Ticket Management Dashboard"
        backTo={`/admin${filterSearch}`}
        backAs="home"
      />
      {/* Full-width layout: the bento grid needs the room. Keep minimal
          horizontal padding so text doesn't touch the viewport edge. */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-4 flex-1 flex flex-col gap-4">
        {/* Prev/Next flush to left/right corners of the content strip. */}
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => prevPath && nav(prevPath)}
            disabled={!prevPath}
            title={prevPath ? "Previous ticket in list" : "No previous ticket"}
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <button
            type="button"
            className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => nextPath && nav(nextPath)}
            disabled={!nextPath}
            title={nextPath ? "Next ticket in list" : "No next ticket"}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>

        {q.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading ticket…" />
          </div>
        ) : q.isError ? (
          <div className="alert-danger">{(q.error as Error)?.message}</div>
        ) : q.data ? (
          <>
            {/* Ticket heading + candidate name span the full viewport width so
                the 60/40 grid below starts flush at Application ID / Application
                Status. */}
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-xl sm:text-2xl font-bold">
                  Responding to Ticket{" "}
                  <span className="font-mono">#{q.data.grievance_id}</span>
                </h1>
                <MarkAsViewedButton
                  currentStatusId={q.data.current_status_id}
                  pending={markViewed.isPending}
                  onClick={() => markViewed.mutate()}
                />
              </div>
              {q.data.candidate_name && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Candidate:{" "}
                  <span className="font-medium text-slate-700 dark:text-slate-200">
                    {q.data.candidate_name}
                  </span>
                </p>
              )}
            </div>

            {/* Bento grid: 60% workspace on the left, 40% context sidebar on
                the right. Default items-stretch equalises column heights on
                lg+; below lg the columns stack and each takes natural height. */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3 flex flex-col gap-4 min-w-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoBox label="Application ID" value={q.data.application_id} mono />
                  <InfoBox
                    label="Submitted On"
                    value={formatDateTime(q.data.sent_date_time)}
                    mono
                  />
                </div>

                <section className="card p-4 space-y-2 lg:min-h-40">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Student's Ticket
                    </h3>
                    {q.data.has_attachment && (
                      <AttachmentInline
                        tabId={tabId}
                        grievanceId={q.data.grievance_id}
                      />
                    )}
                  </div>
                  <p className="whitespace-pre-wrap leading-relaxed text-sm">
                    {q.data.grievance}
                  </p>
                </section>

                <div className="card p-4 lg:min-h-80">
                  <AIResponseEditor
                    mode="real"
                    ticketId={q.data.grievance_id}
                    applicationId={q.data.application_id}
                    botResponse={q.data.bot_response}
                    finalReply={q.data.final_reply}
                    repliedBy={q.data.replied_by}
                    nextTicketPath={nextPath}
                    currentStatusId={q.data.current_status_id}
                  />
                </div>
              </div>

              <aside className="lg:col-span-2 lg:h-full flex flex-col gap-4 min-w-0 min-h-0">
                <ApplicationStatusPanel
                  tabId={tabId}
                  grievanceId={q.data.grievance_id}
                />

                <HistoryDisclosure
                  tabId={tabId}
                  grievanceId={q.data.grievance_id}
                  applicationId={q.data.application_id}
                  scrollWhenOpen
                  openMaxHeightClass="lg:max-h-[26rem]"
                />

                <RightPanelDisclosure
                  title="Response History"
                  icon={<MessageSquare size={14} />}
                  bodyMaxHeightClass="max-h-56"
                >
                  <ResponseHistoryBody actions={q.data.actions} />
                </RightPanelDisclosure>
              </aside>
            </div>
          </>
        ) : null}
      </main>
      <footer className="mt-4 bg-brand-green text-slate-900 text-center text-[11px] py-1.5 px-4">
        © 2026 Synthesys Solutions Pvt Ltd. All rights reserved.
      </footer>
    </div>
  );
}

function MarkAsViewedButton({
  currentStatusId,
  pending,
  onClick,
}: {
  currentStatusId: number | null;
  pending: boolean;
  onClick: () => void;
}) {
  // Hide entirely when the ticket is already replied (status 4) or when the
  // status row is missing – in both cases marking-as-viewed is a no-op the
  // admin should not attempt.
  if (currentStatusId === null || currentStatusId === 4) return null;

  if (currentStatusId === 1) {
    return (
      <button
        type="button"
        className="btn-secondary text-xs px-3 py-1.5"
        disabled
        title="Already viewed"
      >
        <CheckCircle2 size={14} /> Marked as Viewed
      </button>
    );
  }

  return (
    <button
      type="button"
      className="btn-primary text-xs px-3 py-1.5"
      onClick={onClick}
      disabled={pending}
      title="Mark this ticket as viewed to enable sending the reply"
    >
      {pending ? (
        <Spinner label="Marking…" />
      ) : (
        <>
          <Eye size={14} /> Mark as Viewed
        </>
      )}
    </button>
  );
}

function ResponseHistoryBody({ actions }: { actions: GrievanceAction[] }) {
  if (!actions || actions.length === 0) {
    return (
      <div className="p-4 alert-info text-xs">
        No actions recorded on this ticket yet.
      </div>
    );
  }
  return (
    <ul className="p-4 space-y-3">
      {actions.map((a) => (
        <li
          key={a.id}
          className="rounded-md border border-canvas-border dark:border-surface-border p-3 text-sm"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <span className="font-medium">
                {a.admin_full_name || a.admin_username || (
                  <em className="text-slate-400">unknown admin</em>
                )}
              </span>
              <span className="ml-2 text-xs text-slate-500">
                {formatDateTime(a.acted_at)}
              </span>
            </div>
            <ActionBadge type={a.action_type} />
          </div>
          {a.action_type === "edited_submitted" && a.ai_text && a.edited_text && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200">
                Show AI original vs edited
              </summary>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    AI original
                  </p>
                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                    {a.ai_text}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    Submitted
                  </p>
                  <p className="whitespace-pre-wrap text-xs leading-relaxed">
                    {a.edited_text}
                  </p>
                </div>
              </div>
            </details>
          )}
        </li>
      ))}
    </ul>
  );
}

function ActionBadge({ type }: { type: GrievanceAction["action_type"] }) {
  const cfg: Record<GrievanceAction["action_type"], { label: string; className: string }> = {
    generated: {
      label: "Generated AI draft",
      className: "bg-slate-200 text-slate-700 dark:bg-surface-border dark:text-slate-200",
    },
    submitted: {
      label: "Submitted as-is",
      className:
        "bg-brand-green/20 text-status-ackFg dark:bg-brand-green/20 dark:text-brand-green",
    },
    edited_submitted: {
      label: "Edited & submitted",
      className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    },
  };
  const c = cfg[type];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

function InfoBox({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="card px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-1 text-sm ${mono ? "font-mono" : ""} truncate`} title={value}>
        {value}
      </p>
    </div>
  );
}
